import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Heart, MessageCircle, BookmarkPlus, Send, Inbox, PhoneCall } from 'lucide-react';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Avatar, Help, SbiBlock, fmtDate, rise } from './shared';
import ConversationDialog from './ConversationDialog';

const SOURCE_LABEL = { phone: '📞 Telefonat', in_person: '🤝 Persönlich', video: '💻 Video-Call' };

function Thread({ fb, meId }) {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.teamDevThread(fb.id).then((res) => setMsgs(res.data.messages || [])).catch(() => setMsgs([]));
  }, [fb.id]);

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      const res = await api.teamDevThreadPost(fb.id, text.trim());
      setMsgs((m) => [...(m || []), res.data.message]);
      setText('');
    } catch { toast.error('Senden hat nicht geklappt'); }
    finally { setSending(false); }
  };

  return (
    <div className="mt-3 border-t border-border/60 pt-3 space-y-2 td-pop" data-testid={`td-thread-${fb.id}`}>
      <Help>Nachfragen ja, rechtfertigen nein. ‚Kannst du mir ein zweites Beispiel geben?‘ bringt dich weiter als ‚Ja, aber das lag daran, dass …‘.</Help>
      {msgs === null ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : msgs.map((m) => (
        <div key={m.id} className={`text-xs rounded-md px-3 py-2 max-w-[85%] ${m.author_id === meId ? 'bg-emerald-500/10 border border-emerald-500/20 ml-auto' : 'bg-secondary/50 border border-border'}`}>
          <p className="text-[9px] font-mono text-muted-foreground mb-0.5">{m.author_name}</p>
          <p className="text-foreground/90 whitespace-pre-wrap">{m.text}</p>
        </div>
      ))}
      <div className="flex gap-2">
        <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1} placeholder="Deine Nachfrage — nur ihr beide seht sie."
          className="text-sm bg-background rounded-md min-h-[38px]" data-testid={`td-thread-input-${fb.id}`} />
        <Button size="sm" onClick={send} disabled={sending || !text.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-9 px-3" data-testid={`td-thread-send-${fb.id}`}>
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function TakeawayBox({ fb, myGoals, onSaved }) {
  const [text, setText] = useState(fb.takeaway?.text || '');
  const [goalId, setGoalId] = useState(fb.takeaway?.linked_goal_id || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!text.trim()) return toast('Ein Satz reicht — was nimmst du mit?');
    setSaving(true);
    try {
      await api.teamDevTakeaway(fb.id, { text: text.trim(), linked_goal_id: goalId });
      toast.success('Notiert. Genau so wird Feedback zu Wachstum. 🌱');
      onSaved(text.trim(), goalId);
    } catch { toast.error('Speichern hat nicht geklappt'); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-3 border-t border-border/60 pt-3 space-y-2 td-pop" data-testid={`td-takeaway-box-${fb.id}`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={1}
        placeholder="Was nimmst du mit? Ein Satz Erkenntnis …"
        className="text-sm bg-background rounded-md min-h-[38px]" data-testid={`td-takeaway-input-${fb.id}`} />
      {myGoals.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {myGoals.map((g) => (
            <button key={g.id} onClick={() => setGoalId((v) => (v === g.id ? '' : g.id))}
              className={`text-[10px] rounded-full border px-2.5 py-1 transition-colors ${goalId === g.id ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300' : 'border-border text-muted-foreground hover:text-foreground'}`}>
              {goalId === g.id ? '✓ ' : ''}mit Ziel verknüpfen: {g.title}
            </button>
          ))}
        </div>
      )}
      <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full h-7 text-xs" data-testid={`td-takeaway-save-${fb.id}`}>
        Speichern
      </Button>
    </div>
  );
}

function FeedbackCard({ fb, received, meId, myGoals, onThanks, onTakeawaySaved, idx }) {
  const [openPanel, setOpenPanel] = useState(null);
  return (
    <div className={`td-rise td-card bg-card border rounded-lg p-5 ${received && !fb.read_at ? 'border-emerald-500/40' : 'border-border'}`}
      style={rise(idx)} data-testid={`td-feedback-card-${idx}`}>
      {received && (
        <p className="text-[11px] text-emerald-400/90 italic mb-2.5" data-testid={`td-framing-${idx}`}>
          {fb.recorded_by === 'recipient'
            ? `Aus deinem Gespräch mit ${fb.from_name} — von dir festgehalten.`
            : `${fb.from_name} hat sich Zeit genommen, dir beim Besserwerden zu helfen.`}
        </p>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Avatar name={received ? fb.from_name : fb.to_name} />
        <p className="text-sm font-medium text-foreground">
          {received ? fb.from_name : <>an {fb.to_name}</>}
        </p>
        {received && !fb.read_at && (
          <span className="text-[9px] font-mono uppercase border border-emerald-500/40 bg-emerald-500/10 text-emerald-400 rounded px-1.5 py-0.5">Neu</span>
        )}
        {fb.source && (
          <span className="text-[9px] font-mono border border-amber-500/40 bg-amber-500/10 text-amber-400 rounded px-1.5 py-0.5" data-testid={`td-source-badge-${idx}`}>
            {SOURCE_LABEL[fb.source] || fb.source} · Gesprächsnotiz
          </span>
        )}
        {fb.request_topic && (
          <span className="text-[9px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground" title="Antwort auf eine Feedback-Anfrage">zu: {fb.request_topic.slice(0, 40)}</span>
        )}
        {fb.related_goal_title && (
          <span className="text-[9px] font-mono border border-sky-500/40 bg-sky-500/10 text-sky-400 rounded px-1.5 py-0.5">🎯 {fb.related_goal_title.slice(0, 40)}</span>
        )}
        <span className="text-[10px] text-muted-foreground font-mono ml-auto">{fmtDate(fb.created_at)}</span>
      </div>
      <SbiBlock label="Situation" text={fb.situation} color="text-sky-400" />
      <SbiBlock label="Verhalten" text={fb.behavior} color="text-violet-400" />
      <SbiBlock label="Wirkung" text={fb.impact} color="text-amber-400" />
      <SbiBlock label="Vorschlag für nächstes Mal" text={fb.feedforward} color="text-emerald-400" />
      {fb.takeaway && received && (
        <div className="mt-3 bg-emerald-500/5 border border-emerald-500/20 rounded-md px-3 py-2" data-testid={`td-takeaway-shown-${idx}`}>
          <p className="text-[9px] font-mono uppercase text-emerald-400">Das nehme ich mit</p>
          <p className="text-xs text-foreground/90 mt-0.5">{fb.takeaway.text}</p>
        </div>
      )}
      <div className="mt-3.5 pt-3 border-t border-border/60 flex items-center gap-2 flex-wrap">
        {received && fb.recorded_by !== 'recipient' && (fb.thanked_at ? (
          <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Heart className="w-3 h-3 text-amber-400" /> Du hast dich bedankt</span>
        ) : (
          <Button size="sm" variant="outline" onClick={() => onThanks(fb.id)} data-testid={`td-thanks-btn-${idx}`}
            className="h-7 text-xs rounded-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 hover:text-amber-300">
            <Heart className="w-3 h-3 mr-1" /> Danke sagen
          </Button>
        ))}
        {fb.recorded_by !== 'recipient' && (
          <Button size="sm" variant="outline" onClick={() => setOpenPanel((p) => (p === 'thread' ? null : 'thread'))} data-testid={`td-thread-btn-${idx}`}
            className="h-7 text-xs rounded-full">
            <MessageCircle className="w-3 h-3 mr-1" /> Nachfrage stellen{fb.thread_count > 0 ? ` (${fb.thread_count})` : ''}
          </Button>
        )}
        {received && !fb.takeaway && (
          <Button size="sm" variant="outline" onClick={() => setOpenPanel((p) => (p === 'takeaway' ? null : 'takeaway'))} data-testid={`td-takeaway-btn-${idx}`}
            className="h-7 text-xs rounded-full">
            <BookmarkPlus className="w-3 h-3 mr-1" /> Das nehme ich mit
          </Button>
        )}
      </div>
      {openPanel === 'thread' && <Thread fb={fb} meId={meId} />}
      {openPanel === 'takeaway' && received && (
        <TakeawayBox fb={fb} myGoals={myGoals} onSaved={(text, goalId) => { onTakeawaySaved(fb.id, text, goalId); setOpenPanel(null); }} />
      )}
    </div>
  );
}

const REACTIONS = [
  { v: 'try', label: '🚀 Probier ich aus' },
  { v: 'later', label: '🕐 Interessant, später' },
  { v: 'no_fit', label: '🙏 Passt für mich nicht' },
];
const REACTION_LABEL = { try: '🚀 Probierst du aus', later: '🕐 Für später gemerkt', no_fit: '🙏 Passt nicht — völlig okay' };

function IdeaCard({ idea, onReact, i }) {
  return (
    <div className="td-rise td-card bg-card border border-violet-500/25 rounded-lg p-4" style={rise(i)} data-testid={`td-idea-card-${i}`}>
      <p className="text-xs text-muted-foreground">
        💡 Idee von <span className="font-medium text-foreground">{idea.author_name}</span>
        {idea.goal_title && <> zu deinem Ziel <span className="font-medium text-foreground">„{idea.goal_title}“</span></>}
        <span className="font-mono text-[9px] ml-2">{fmtDate(idea.created_at)}</span>
      </p>
      <p className="text-sm text-foreground/90 mt-1.5 leading-relaxed">{idea.text}</p>
      {idea.reaction ? (
        <p className="text-[11px] text-violet-300 mt-2.5" data-testid={`td-idea-reacted-${i}`}>{REACTION_LABEL[idea.reaction]}{idea.try_outcome ? ' · Rückblick notiert ✓' : ''}</p>
      ) : (
        <div className="mt-3 space-y-1.5">
          <div className="flex flex-wrap gap-2">
            {REACTIONS.map((r) => (
              <button key={r.v} onClick={() => onReact(idea.id, r.v)} data-testid={`td-idea-react-${i}-${r.v}`}
                className="text-xs rounded-full border border-border px-3 py-1.5 text-foreground hover:border-violet-500/50 hover:bg-violet-500/10 transition-colors">
                {r.label}
              </button>
            ))}
          </div>
          <Help>Der Ideengeber sieht nur, dass du reagiert hast — nicht wie. ‚Passt nicht‘ ist völlig okay und braucht keine Begründung.</Help>
        </div>
      )}
    </div>
  );
}

export default function InboxTab({ meId, members = [], refreshCounts, gotoTab }) {
  const [view, setView] = useState('received');
  const [items, setItems] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [myGoals, setMyGoals] = useState([]);
  const [convOpen, setConvOpen] = useState(false);
  const viewRef = React.useRef(view);

  const load = useCallback((v) => {
    (v === 'received' ? api.teamDevInbox() : api.teamDevSent())
      .then(async (res) => {
        if (viewRef.current !== v) return;
        const list = res.data.items || [];
        setItems(list);
        if (v === 'received') {
          setIdeas(res.data.ideas || []);
          const unread = list.filter((f) => !f.read_at);
          if (unread.length) {
            await Promise.all(unread.map((f) => api.teamDevMarkRead(f.id).catch(() => {})));
            if (viewRef.current !== v) return;
            const now = new Date().toISOString();
            setItems((its) => (its || list).map((f) => (f.read_at ? f : { ...f, read_at: now })));
            refreshCounts();
          }
        }
      })
      .catch(() => toast.error('Postfach konnte nicht geladen werden'));
    api.teamDevGoals().then((res) => setMyGoals((res.data.active || []).filter((g) => g.is_mine))).catch(() => {});
  }, [refreshCounts]);

  useEffect(() => { viewRef.current = view; setItems(null); load(view); }, [view, load]);

  const thanks = async (id) => {
    try {
      await api.teamDevThanks(id);
      setItems((its) => its.map((f) => (f.id === id ? { ...f, thanked_at: new Date().toISOString() } : f)));
      toast.success('Dein Dankeschön ist unterwegs 💛');
    } catch { toast.error('Das hat leider nicht geklappt'); }
  };

  const react = async (ideaId, reaction) => {
    try {
      await api.teamDevIdeaReact(ideaId, reaction);
      setIdeas((is) => is.map((i) => (i.id === ideaId ? { ...i, reaction, reacted_at: new Date().toISOString() } : i)));
      refreshCounts();
      toast.success(reaction === 'try' ? 'Notiert — im nächsten Check-in fragen wir, wie es lief. 🚀' : 'Reaktion gespeichert.');
    } catch { toast.error('Das hat nicht geklappt'); }
  };

  const onTakeawaySaved = (fid, text, goalId) => {
    setItems((its) => its.map((f) => (f.id === fid ? { ...f, takeaway: { text, linked_goal_id: goalId } } : f)));
  };

  const received = view === 'received';
  const grouped = {};
  (items || []).forEach((f) => {
    const key = received && f.related_goal_title ? `🎯 Zu deinem Ziel: ${f.related_goal_title}` : '';
    (grouped[key] = grouped[key] || []).push(f);
  });
  const groupKeys = Object.keys(grouped).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));

  return (
    <div className="space-y-4 max-w-3xl" data-testid="td-inbox">
      <div className="flex gap-1 items-center flex-wrap">
        {[['received', 'Erhalten'], ['sent', 'Gesendet']].map(([v, l]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`td-inbox-view-${v}`}
            className={`px-3 py-1.5 rounded-full text-[11px] font-mono uppercase tracking-wider border transition-colors ${
              view === v ? 'bg-secondary text-foreground border-border' : 'text-muted-foreground border-transparent hover:text-foreground'}`}>
            {l}
          </button>
        ))}
        <button onClick={() => setConvOpen(true)} data-testid="td-conversation-btn"
          className="ml-auto flex items-center gap-1.5 text-xs font-medium text-amber-400 border border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/15 rounded-full px-3.5 py-1.5 transition-colors">
          <PhoneCall className="w-3.5 h-3.5" /> Gespräch festhalten
        </button>
      </div>

      {received && ideas.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">Erhaltene Ideen</p>
          {ideas.map((idea, i) => <IdeaCard key={idea.id} idea={idea} onReact={react} i={i} />)}
        </div>
      )}

      {items === null ? (
        <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-lg p-10 text-center td-rise" data-testid="td-inbox-empty">
          <Inbox className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
          {received ? (
            <>
              <p className="text-sm text-foreground">Noch kein Feedback erhalten.</p>
              <p className="text-xs text-muted-foreground mt-1">Tipp: Warte nicht — fordere welches an. Erbetenes Feedback ist das wertvollste.</p>
              <button onClick={() => gotoTab('requests')} data-testid="td-inbox-empty-cta"
                className="mt-3 text-xs font-medium text-emerald-400 border border-emerald-500/30 bg-emerald-500/5 hover:bg-emerald-500/15 rounded-full px-4 py-1.5 transition-colors">
                Feedback anfordern
              </button>
            </>
          ) : (
            <>
              <p className="text-sm text-foreground">Noch kein Feedback gegeben.</p>
              <p className="text-xs text-muted-foreground mt-1">Jemandem ist etwas Gutes aufgefallen? Sag es — es kommt besser an, als du denkst.</p>
            </>
          )}
        </div>
      ) : (
        groupKeys.map((gk) => (
          <div key={gk || 'none'} className="space-y-2.5">
            {gk && <p className="text-[10px] font-mono uppercase tracking-wider text-sky-400">{gk}</p>}
            {grouped[gk].map((fb) => (
              <FeedbackCard key={fb.id} fb={fb} idx={(items || []).findIndex((item) => item.id === fb.id)} received={received} meId={meId} myGoals={myGoals}
                onThanks={thanks} onTakeawaySaved={onTakeawaySaved} />
            ))}
          </div>
        ))
      )}

      {convOpen && (
        <ConversationDialog members={members} meId={meId} myGoals={myGoals}
          onClose={() => setConvOpen(false)} onSaved={() => { load(view); refreshCounts(); }} />
      )}
    </div>
  );
}
